<template>
  <div class="mt-4 mb-6 flex gap-4">
    <Listbox
      v-model="atsFilter"
      as="div"
      class="flex w-56 flex-col gap-x-2 lg:flex-row lg:items-center"
    >
      <ListboxLabel class="block shrink-0 text-sm font-medium text-gray-700">
        {{ t('company.importer.ats') }}
      </ListboxLabel>
      <div class="relative md:w-full">
        <ListboxButton
          class="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pr-10 pl-3 text-left shadow-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-hidden sm:text-sm"
        >
          <span
            class="ml-2 block truncate capitalize"
            :class="{ 'text-gray-500': !atsFilter }"
            >{{ atsFilter?.label || t('company.importer.allATS') }}</span
          >
          <span
            class="pointer-events-none absolute inset-y-0 right-0 ml-2 flex items-center pr-2"
          >
            <ChevronUpDownIcon
              class="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </span>
        </ListboxButton>

        <transition
          leaveActiveClass="transition ease-in duration-100"
          leaveFromClass="opacity-100"
          leaveToClass="opacity-0"
        >
          <ListboxOptions
            class="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
          >
            <ListboxOption
              value=""
              class="hover:bg-indigo-600 hover:text-white"
              :class="[
                !atsFilter ? 'bg-indigo-600 text-white' : 'text-gray-900',
                'relative cursor-default py-2 pr-9 pl-3 select-none',
              ]"
              >{{ t('company.importer.allATS') }}</ListboxOption
            >
            <ListboxOption
              v-for="(ats, index) in importerStore.availableATS"
              :key="index"
              v-slot="{ active, selected }"
              as="template"
              :value="ats"
            >
              <li
                :class="[
                  active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                  'relative cursor-default py-2 pr-9 pl-3 select-none',
                ]"
              >
                <span
                  :class="[
                    selected ? 'font-semibold' : 'font-normal',
                    'block truncate capitalize',
                  ]"
                >
                  {{ ats.label }}
                </span>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </transition>
      </div>
    </Listbox>

    <div class="flex grow flex-col gap-x-2 lg:flex-row lg:items-center">
      <label for="company" class="block text-sm font-medium text-gray-700">{{
        t('jobAlerts.search.company')
      }}</label>
      <Multiselect
        id="company"
        v-model="company"
        class="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        :placeholder="t('vacancy.company.placeholder')"
        :minChars="0"
        :filterResults="false"
        :resolveOnLoad="true"
        :delay="500"
        :searchable="true"
        :options="searchCompany"
        :object="true"
        :noOptionsText="t('vacancy.company.noOptions')"
        valueProp="id"
        label="name"
        trackBy="name"
        inputType="search"
        autocomplete="off"
      />
    </div>

    <div
      class="col-span-12 flex flex-col items-start gap-x-2 lg:col-span-4 lg:flex-row lg:items-center"
    >
      <label
        for="dateRange"
        class="block shrink-0 text-sm font-medium text-gray-700"
        >{{ t('candidates.dateRange') }}</label
      >

      <DatePicker
        :inputAttr="{ id: 'dateRange' }"
        range
        :shortcuts="shortcuts"
        :value="importerStore.dateRangeFilter"
        type="dateTime"
        format="D MMM YYYY"
        inputClass="focus:border-indigo-500 border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 w-full block rounded-md shadow-xs focus:outline-hidden sm:text-sm"
        :disabledDate="disabledEndDate"
        :clearable="true"
        :editable="false"
        class="mt-1"
        @change="updateDateRange"
      />
    </div>

    <Listbox
      v-model="statusFilter"
      as="div"
      class="flex w-56 flex-col gap-x-2 lg:flex-row lg:items-center"
      multiple
    >
      <ListboxLabel class="block shrink-0 text-sm font-medium text-gray-700">
        {{ t('company.events.status') }}
      </ListboxLabel>
      <div class="relative md:w-full">
        <ListboxButton
          class="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pr-10 pl-3 text-left shadow-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-hidden sm:text-sm"
        >
          <span class="ml-2 block truncate">{{
            statusFilter.length
              ? t('company.events.selected', {
                  count: statusFilter.length,
                })
              : t('candidates.filters.all')
          }}</span>
          <span
            class="pointer-events-none absolute inset-y-0 right-0 ml-2 flex items-center pr-2"
          >
            <ChevronUpDownIcon
              class="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </span>
        </ListboxButton>

        <transition
          leaveActiveClass="transition ease-in duration-100"
          leaveFromClass="opacity-100"
          leaveToClass="opacity-0"
        >
          <ListboxOptions
            class="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
          >
            <ListboxOption
              v-for="(status, index) in possibleStates"
              :key="index"
              v-slot="{ active, selected }"
              as="template"
              :value="status.code"
            >
              <li
                class="relative flex cursor-default gap-2 py-2 pr-9 pl-3 text-gray-900 select-none"
                :class="[active ? 'bg-indigo-600 text-white' : 'text-gray-900']"
              >
                <CheckIcon v-if="selected" class="h-5 w-5 text-indigo-600" />
                <div v-else class="w-5"></div>

                <span
                  class="capitalize"
                  :class="[
                    selected ? 'font-semibold' : 'font-normal',
                    'block truncate',
                  ]"
                >
                  {{ status.code.toLowerCase() }}
                </span>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </transition>
      </div>
    </Listbox>
  </div>
</template>

<script setup>
import {
  Listbox,
  ListboxButton,
  ListboxLabel,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/vue'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid'
import { useImporterStore } from '../../store/importerStore'
import { computed, onBeforeMount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import tooManagement from '../../api/tooManagement'
import Multiselect from '@vueform/multiselect'
import { possibleStates } from '../../utils/vacancyImportStates'
import DatePicker from 'vue-datepicker-next'
import 'vue-datepicker-next/index.css'
import {
  isAfter,
  startOfDay,
  startOfWeek,
  endOfDay,
  endOfWeek,
  subDays,
  subWeeks,
  subMonths,
  subYears,
  startOfMonth,
  startOfYear,
  endOfYear,
  endOfMonth,
  setDefaultOptions,
} from 'date-fns'
import { useRoute } from 'vue-router'
import router from '../../router'

const importerStore = useImporterStore()
const route = useRoute()
const { t } = useI18n({
  inheritLocale: true,
  useScope: 'global',
})
setDefaultOptions({ weekStartsOn: 1 })

const atsFilter = ref({ value: '' })

const company = ref({})
const searchCompany = async (filter = '') => {
  const queryRes = await tooManagement.getCompanies({
    filter: filter || '',
    size: 15,
  })
  return queryRes.data
}

const statusFilter = computed({
  get() {
    return importerStore.filter.statuses || []
  },
  set(value) {
    importerStore.filter.statuses = value
    performSearch()
  },
})

const shortcuts = [
  {
    text: t('candidates.dateFilter.today'),
    onClick() {
      const today = new Date()
      return [startOfDay(today), endOfDay(today)]
    },
  },
  {
    text: t('candidates.dateFilter.yesterday'),
    onClick() {
      const yesterday = subDays(new Date(), 1)
      return [startOfDay(yesterday), endOfDay(yesterday)]
    },
  },
  {
    text: t('candidates.dateFilter.thisWeek'),
    onClick() {
      const today = new Date()
      const week = startOfWeek(new Date())
      return [week, today]
    },
  },
  {
    text: t('candidates.dateFilter.lastWeek'),
    onClick() {
      const lastWeek = subWeeks(new Date(), 1)
      return [startOfWeek(lastWeek), endOfWeek(lastWeek)]
    },
  },
  {
    text: t('candidates.dateFilter.thisMonth'),
    onClick() {
      const monthStart = startOfMonth(new Date())
      return [monthStart, new Date()]
    },
  },
  {
    text: t('candidates.dateFilter.lastMonth'),
    onClick() {
      const lastMonth = subMonths(new Date(), 1)
      return [startOfMonth(lastMonth), endOfMonth(lastMonth)]
    },
  },
  {
    text: t('candidates.dateFilter.thisYear'),
    onClick() {
      const yearStart = startOfYear(new Date())
      return [yearStart, new Date()]
    },
  },
  {
    text: t('candidates.dateFilter.lastYear'),
    onClick() {
      const lastYear = subYears(new Date(), 1)
      return [startOfYear(lastYear), endOfYear(lastYear)]
    },
  },
]

const updateDateRange = (value) => {
  importerStore.dateRangeFilter[0] = value[0]
  importerStore.dateRangeFilter[1] = value[1]
  performSearch()
}

const disabledEndDate = (date) => isAfter(date, new Date())

const performSearch = () => {
  router.push({
    query: {
      ...route.params.query,
      company: importerStore.filter.companyIds[0]?.name,
      companyId: importerStore.filter.companyIds[0]?.id,
      startDate: importerStore.dateRangeFilter[0]?.toISOString(),
      endDate: importerStore.dateRangeFilter[1]?.toISOString(),
      ats: importerStore.filter.atsNames[0],
      status: importerStore.filter.statuses,
    },
  })

  importerStore.page.number = 1
  importerStore.loadAllEvents()
}

watch(company, (newCompany) => {
  importerStore.filter.companyIds = newCompany?.id ? [newCompany] : []
  performSearch()
})

watch(atsFilter, (newAts) => {
  importerStore.filter.atsNames = newAts ? [newAts.value] : []
  performSearch()
})

onBeforeMount(() => {
  if (route.query.status) {
    importerStore.filter.statuses = Array.isArray(route.query.status)
      ? route.query.status
      : [route.query.status]
  }
  if (route.query.companyId) {
    importerStore.filter.companyIds = [route.query.companyId]
    company.value = { id: route.query.companyId, name: route.query.company }
  }
  if (route.query.ats) {
    importerStore.filter.atsNames = [route.query.ats]
    atsFilter.value = importerStore.availableATS.find(
      (ats) => ats.value === route.query.ats,
    )
  }
  if (route.query.startDate && route.query.endDate) {
    importerStore.dateRangeFilter[0] = new Date(route.query.startDate)
    importerStore.dateRangeFilter[1] = new Date(route.query.endDate)
  }

  importerStore.page.number = 1
  importerStore.loadAllEvents()
})
</script>
